import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { guideList } from '../data/guides';
import { countyGuideList } from '../data/countyGuides';
import SEOHelmet from './SEOHelmet';
import './GuidesPage.css';

const categoryFor = (guide) => {
  if (guide.category) return guide.category;
  const text = `${guide.slug} ${guide.title}`.toLowerCase();
  if (/sell|marketing/.test(text)) return 'Seller Guides';
  if (/insurance|flood|homestead|reserve|hoa|inspection/.test(text)) return 'Owner Resources';
  if (/moving|relocation|commut|historic|country.club|active.adult|equestrian|seasonal|waterfront|oceanfront/.test(text)) return 'Lifestyle & Relocation';
  return 'Buyer & Investment Guides';
};

const categoryOrder = ['Buyer & Investment Guides', 'Seller Guides', 'Lifestyle & Relocation', 'Owner Resources'];
const countyGroups = [
  { name: 'Palm Beach County', buyer: 'palm-beach-county-home-buying-guide', seller: 'selling-home-palm-beach-county' },
  ...[...new Set(countyGuideList.map((guide) => guide.county))].map((county) => ({
    name: county,
    buyer: countyGuideList.find((guide) => guide.county === county && guide.category === 'Buyer & Investment Guides').slug,
    seller: countyGuideList.find((guide) => guide.county === county && guide.category === 'Seller Guides').slug
  }))
];

const GuidesPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  const grouped = categoryOrder.map((category) => ({
    category,
    guides: guideList.filter((guide) => categoryFor(guide) === category)
  }));

  return (
    <main className="guides-index">
      <SEOHelmet
        title={`South Florida Buyer, Seller & Lifestyle Guides | ${SITE_CONFIG.AGENT.BROKERAGE}`}
        description="Explore practical real estate guides for buyers, sellers, investors, and homeowners across Palm Beach County and South Florida."
        path="/guides"
      />
      <header className="guides-index-hero">
        <button type="button" onClick={() => navigate('/')} className="guides-home-button">← Home</button>
        <span>South Florida Resource Library</span>
        <h1>Real estate guidance for the decisions behind the move.</h1>
        <p>Explore buyer, seller, lifestyle, relocation, insurance, association, and property-diligence resources—then connect with Peter for advice tailored to your situation.</p>
        <div className="guides-county-line">Miami-Dade · Broward · Palm Beach · Martin · St. Lucie</div>
      </header>

      <div className="guides-index-content">
        <section className="guides-county-section" aria-labelledby="county-guides-title">
          <div className="guides-category-heading"><h2 id="county-guides-title">Start with your county</h2></div>
          <div className="guides-county-grid">
            {countyGroups.map((county) => (
              <nav className="guides-county-card" key={county.name} aria-label={`${county.name} guides`}>
                <h3>{county.name}</h3>
                <Link to={`/guide/${county.buyer}`}>Buying a home <span aria-hidden="true">→</span></Link>
                <Link to={`/guide/${county.seller}`}>Selling a home <span aria-hidden="true">→</span></Link>
              </nav>
            ))}
          </div>
        </section>
        {grouped.map(({ category, guides }) => (
          <section className="guides-category" key={category}>
            <div className="guides-category-heading">
              <h2>{category}</h2>
              <span>{guides.length} resources</span>
            </div>
            <div className="guides-card-grid">
              {guides.map((guide) => (
                <Link to={`/guide/${guide.slug}`} className="guide-index-card" key={guide.slug}>
                  <span>{guide.eyebrow}</span>
                  <h3>{guide.title}</h3>
                  <p>{guide.metaDescription}</p>
                  <strong>Read guide <span aria-hidden="true">→</span></strong>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default GuidesPage;
